import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FlashcardViewer from "@/components/FlashcardViewer";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Trash2, Layers } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import SEO from "@/components/SEO";

interface FlashcardSet {
  id: string;
  title: string;
  created_at: string;
  flashcards: {
    id: string;
    question: string;
    answer: string;
  }[];
}

const Flashcards = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sets, setSets] = useState<FlashcardSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSet, setSelectedSet] = useState<FlashcardSet | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchFlashcardSets();
    }
  }, [user]);

  const fetchFlashcardSets = async () => {
    try {
      const { data: setsData, error: setsError } = await supabase
        .from("flashcard_sets")
        .select("id, title, created_at")
        .order("created_at", { ascending: false });

      if (setsError) throw setsError;

      const setsWithCards = await Promise.all(
        (setsData || []).map(async (set) => {
          const { data: cards } = await supabase
            .from("flashcards")
            .select("id, question, answer")
            .eq("set_id", set.id);
          return { ...set, flashcards: cards || [] };
        })
      );

      setSets(setsWithCards);
    } catch (error) {
      console.error("Error fetching flashcard sets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSet = async (setId: string) => {
    try {
      const { error } = await supabase
        .from("flashcard_sets")
        .delete()
        .eq("id", setId);

      if (error) throw error;

      setSets((prev) => prev.filter((s) => s.id !== setId));
      if (selectedSet?.id === setId) {
        setSelectedSet(null);
      }
      toast({ title: "Flashcard set deleted" });
    } catch (error) {
      console.error("Error deleting set:", error);
      toast({ title: "Failed to delete", variant: "destructive" });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="My Flashcards — NoteGenius"
        description="Study your AI-generated flashcard sets. Flip, shuffle, and review cards built from your own notes."
        path="/flashcards"
      />
      <Navbar />
      <main className="flex-1 container mx-auto px-4 py-8">
        {selectedSet ? (
          <div className="max-w-2xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => setSelectedSet(null)}
              className="mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to sets
            </Button>
            <FlashcardViewer
              cards={selectedSet.flashcards}
              title={selectedSet.title}
            />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold">My Flashcards</h1>
                <p className="text-muted-foreground mt-1">
                  Study your generated flashcard sets
                </p>
              </div>
            </div>

            {sets.length === 0 ? (
              <div className="text-center py-16 glass rounded-2xl">
                <Layers className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                <h2 className="text-xl font-semibold mb-2">No flashcard sets yet</h2>
                <p className="text-muted-foreground mb-6">
                  Generate flashcards from your notes to start studying
                </p>
                <Button onClick={() => navigate("/#upload")}>
                  Upload Notes
                </Button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {sets.map((set) => (
                  <div
                    key={set.id}
                    className="glass rounded-xl p-5 hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={() => setSelectedSet(set)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{set.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {set.flashcards.length} cards
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(set.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Delete flashcard set"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSet(set.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Flashcards;
